/*
 * This function merges three objects together, with the following priority:
 * 1. The default values
 * 2. The `provided` object
 * 3. The overrides object
 *
 * The merging skips keys with undefined values in the object being merged in.
 * This function can replace function definitions like this:
 *
 * function fooNew({
 *   x = 0,
 *   y = 0,
 *   z,
 *   w,
 * }:Partial<Foo>): Foo {
 *   return {
 *     x, y, z, w, q: calculateInitialQ(),
 *   }
 * }
 *
 * with this:
 *
 * function fooNew(partialPrototype: Partial<Foo>): Foo {
 *   return mergeWithDefaultsAndOverrides(
 *     {x:0, y:0},
 *     partialPrototype,
 *     {q: calculateInitialQ()}
 *   );
 * }
 *
 * Most benefit is achieved when there are many fields of Foo, few of them
 * with default values or calculated initial values, as it saves the deconstruction
 * of the partial prototype and the manual merging of the fields.
 */
export function mergeWithDefaultsAndOverrides<T>(defaults: Partial<T>, provided: Partial<T>, overrides: Partial<T>): T {
  const merged = defaults as Record<string, any>;
  for (const [k, v] of Object.entries(provided)) {
    if (v !== undefined) {
      merged[k] = v;
    }
  }
  for (const [k, v] of Object.entries(overrides)) {
    if (v !== undefined) {
      merged[k] = v;
    }
  }
  return merged as T;
}
